import { SESSIONS_TEST_IDS } from '@/components/admin/sessions/contracts';
import { MarkedText } from '@/components/admin/sessions/marked-text';
import { hiddenHitsLabel, hitPlace, moreHitsLabel } from '@/lib/session-search';
import { conversationUrl } from '@matter_extractor/shared';
import type { SessionSearchHit } from '@matter_extractor/shared';
import { useState } from 'react';

/**
 * The line « trouvé dans ... » under the referents of a retained line
 * (`recherche-sessions.md` §5, D3): WHERE the words were found when it is not in a
 * column the line already shows.
 *
 * A reply's link is a plain anchor, as the extracts of Matière are: the address comes from
 * `conversationUrl` of `shared`, the ONE place the 1-based `?tour=` of §12.3 is spelled
 * (a typed `<Link search>` would write the number as a quoted string, `?tour=%224%22`).
 *
 * The first place is always there - a field by its label, a reply by a link to its
 * turn of the conversation, the identifier - with the excerpt between « » and the
 * passages in bold bleu nuit. When the session has more (`count` is the total, the
 * hits are the twenty served), « + N autres endroits » unfolds them IN PLACE, « et
 * dans ... », and « Replier » folds them back. The unfolded state is the row's, not
 * the address's.
 *
 * ## Above the veil
 *
 * The experience's link lays a veil over the whole row (`sessions-table.tsx`), so
 * what must take the pointer - the link to a turn and the two buttons - stands
 * above it (`relative z-10`), the régime of `person-name.tsx` and `noyau-pill.tsx`.
 * The words around them are not interactive and stay under it.
 */

export interface FoundInProps {
  readonly sessionId: string;
  /** The places served, in the order of §5, twenty at most. */
  readonly hits: readonly SessionSearchHit[];
  /** How many places there are in all (`searchHitCount`). */
  readonly count: number;
}

const LINE = 'text-caption text-gray-700';
const ACTION =
  'relative z-10 cursor-pointer text-caption text-bleu-nuit underline underline-offset-2 hover:text-bleu-nuit-deep focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

export function FoundIn({ sessionId, hits, count }: FoundInProps) {
  const [open, setOpen] = useState(false);
  const first = hits[0];
  if (first === undefined) return null;
  const others = Math.max(count, hits.length) - 1;
  const rest = hits.slice(1);
  const hidden = Math.max(count - hits.length, 0);

  return (
    <div
      data-testid={SESSIONS_TEST_IDS.rowFound}
      data-count={Math.max(count, hits.length)}
      data-open={open ? 'true' : 'false'}
      className="mt-1 flex flex-col gap-0.5"
    >
      <FoundLine sessionId={sessionId} hit={first} lead="trouvé dans" />
      {open ? (
        <>
          {rest.map((hit, at) => (
            <FoundLine
              // A place is a field, a reply or the identifier once each: its order is its identity.
              // biome-ignore lint/suspicious/noArrayIndexKey: the places arrive in a fixed order
              key={at}
              sessionId={sessionId}
              hit={hit}
              lead="et dans"
            />
          ))}
          {hidden > 0 ? (
            <p data-testid={SESSIONS_TEST_IDS.rowFoundRest} className={LINE}>
              {hiddenHitsLabel(hidden)}
            </p>
          ) : null}
          <div>
            <button
              type="button"
              data-testid={SESSIONS_TEST_IDS.rowFoundLess}
              aria-expanded="true"
              onClick={() => setOpen(false)}
              className={ACTION}
            >
              Replier
            </button>
          </div>
        </>
      ) : others > 0 ? (
        <div>
          <button
            type="button"
            data-testid={SESSIONS_TEST_IDS.rowFoundMore}
            aria-expanded="false"
            onClick={() => setOpen(true)}
            className={ACTION}
          >
            {moreHitsLabel(others)}
          </button>
        </div>
      ) : null}
    </div>
  );
}

function FoundLine({
  sessionId,
  hit,
  lead,
}: {
  sessionId: string;
  hit: SessionSearchHit;
  lead: string;
}) {
  const place = hitPlace(hit);
  return (
    <p data-testid={SESSIONS_TEST_IDS.rowFoundLine} data-kind={hit.kind} className={LINE}>
      {lead}{' '}
      {place.kind === 'reply' ? (
        <a
          href={conversationUrl(sessionId, place.turn - 1)}
          data-testid={SESSIONS_TEST_IDS.rowFoundTurn}
          className="relative z-10 text-bleu-nuit underline-offset-2 hover:underline"
        >
          {place.words}
        </a>
      ) : (
        place.words
      )}
      {' : « '}
      <MarkedText text={hit.excerpt} spans={hit.spans} />
      {' »'}
    </p>
  );
}
